import AppDialog from '@components/app-dialog/app-dialog.tsx';
import { Button } from '@mui/material';
import React, { useState } from 'react';
import DialogContentComponent from './dialog-content.tsx';
import DialogActionsComponent from './dialog-actions.tsx';
import DeleteDialog from '@components/delete-dialog/delete-dialog.tsx';
import { AppDialogProps } from '@models/app-dialog.model.ts';
import { useTranslation } from 'react-i18next';

type DialogSize = NonNullable<AppDialogProps['size']>;
const SIZES: DialogSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

const Group = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <p className="font-semibold text-gray-700">{title}</p>
    <div className="flex flex-wrap items-center gap-4">{children}</div>
  </div>
);

const DialogComponent: React.FC = () => {
  const { t } = useTranslation();
  const [name, setName] = useState('');

  const [openNormal, setOpenNormal] = useState(false);
  const [openNoBackdrop, setOpenNoBackdrop] = useState(false);
  const [openCenter, setOpenCenter] = useState(false);
  const [openSize, setOpenSize] = useState<DialogSize | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showDeleteLoading, setShowDeleteLoading] = useState(false);

  const content = (
    <DialogContentComponent
      value={name}
      onChange={(e) => setName(e.target.value)}
    />
  );

  return (
    <div className="flex flex-col gap-10">
      <h2 className="text-xl font-bold text-gray-800">Dialog</h2>

      <Group title="AppDialog">
        <Button variant="contained" onClick={() => setOpenNormal(true)}>
          Normal
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => setOpenNoBackdrop(true)}
        >
          No backdrop close
        </Button>
        <Button variant="outlined" onClick={() => setOpenCenter(true)}>
          Centered title
        </Button>
      </Group>

      <Group title="Sizes">
        {SIZES.map((size) => (
          <Button
            key={size}
            variant="outlined"
            onClick={() => setOpenSize(size)}
          >
            {size.toUpperCase()}
          </Button>
        ))}
      </Group>

      <Group title="DeleteDialog">
        <Button
          className="bg-red-500!"
          variant="contained"
          onClick={() => setShowDelete(true)}
        >
          Delete
        </Button>
        <Button
          className="bg-red-500!"
          variant="contained"
          onClick={() => setShowDeleteLoading(true)}
        >
          Delete (loading)
        </Button>
      </Group>

      {/* ---- instances ---- */}
      <AppDialog
        open={openNormal}
        title="Subscribe"
        content={content}
        actions={
          <DialogActionsComponent
            onSave={() => setOpenNormal(false)}
            onCancel={() => setOpenNormal(false)}
          />
        }
        onClose={() => setOpenNormal(false)}
      />

      <AppDialog
        open={openNoBackdrop}
        disableBackdropClick
        title="Subscribe"
        content={content}
        actions={
          <DialogActionsComponent
            onSave={() => setOpenNoBackdrop(false)}
            onCancel={() => setOpenNoBackdrop(false)}
          />
        }
        onClose={() => setOpenNoBackdrop(false)}
      />

      <AppDialog
        open={openCenter}
        title="Centered title"
        titleAlign="center"
        content={content}
        actions={
          <DialogActionsComponent
            onSave={() => setOpenCenter(false)}
            onCancel={() => setOpenCenter(false)}
          />
        }
        onClose={() => setOpenCenter(false)}
      />

      <AppDialog
        open={!!openSize}
        size={openSize ?? 'md'}
        title={`Size: ${openSize?.toUpperCase() ?? ''}`}
        content={content}
        actions={
          <DialogActionsComponent
            onSave={() => setOpenSize(null)}
            onCancel={() => setOpenSize(null)}
          />
        }
        onClose={() => setOpenSize(null)}
      />

      <DeleteDialog
        open={showDelete}
        title={t('delete_popup_title')}
        isLoading={false}
        onDelete={() => setShowDelete(false)}
        onCancel={() => setShowDelete(false)}
      />

      <DeleteDialog
        open={showDeleteLoading}
        title={t('delete_popup_title')}
        isLoading
        onDelete={() => setShowDeleteLoading(false)}
        onCancel={() => setShowDeleteLoading(false)}
      />
    </div>
  );
};

export default DialogComponent;
